import { ChevronRight, Edit3, ArrowLeft, Heart } from 'lucide-react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { downloadImageWithWatermarks } from '../../lib/download-utils';
import { DeleteFileDialog } from './delete-file-dialog';
import { PageHeader } from './page-header';
import { PhotoBadge, PhotoCategory } from './photo-badge';
import { AddPhotoPlaceholder } from './add-photo-placeholder';
import { GeneratingPhotoPlaceholder } from './generating-photo-placeholder';
import { useState } from 'react';
import { Check, X, MoreVertical, Eye, Download, Share2, Trash2, Search } from 'lucide-react';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

interface PropertyDetailProps {
  property: {
    id: string;
    address: string;
    photoCount: number;
    date: string;
    imageUrl: string;
    thumbnails?: string[];
    galleryImages?: Array<{
      url: string;
      category: PhotoCategory;
      aspectRatio?: 'landscape' | 'portrait' | 'square';
      variantCount?: number; // Number of render variants in this session
      roomName?: string; // Room name for this photo
      originalUrl?: string; // Original image URL
      favorite?: boolean;
      generationError?: boolean;
      variants?: Array<{
        id: string;
        url: string;
        label: string;
      }>;
    }>;
  };
  onBack: () => void;
  onPhotoClick?: (photoUrl: string, photoIndex: number, variantCount?: number, roomName?: string, variants?: Array<{ id: string; url: string; label: string }>, originalUrl?: string, generationId?: string) => void;
  onAddPhotoClick?: () => void;
  onAddressUpdate?: (newAddress: string) => void;
  onDeletePhoto?: (photoUrl: string) => void;
  onToggleFavorite?: (photoUrl: string) => void;
  isGenerating?: boolean;
  generatingPropertyId?: string | null;
  generatingImageId?: string | null;
}

export function PropertyDetail({ property, onBack, onPhotoClick, onAddPhotoClick, onAddressUpdate, onDeletePhoto, onToggleFavorite, isGenerating, generatingPropertyId, generatingImageId }: PropertyDetailProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [newAddress, setNewAddress] = useState(property.address);
  const [searchQuery, setSearchQuery] = useState('');

  const rawGalleryImages = property.galleryImages || [];
  const galleryImages = rawGalleryImages.filter((img) => {
    if (img.deleted) return false;
    if (searchQuery) {
      return img.roomName?.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });
  const [fileToDelete, setFileToDelete] = useState<{ url: string; name: string } | null>(null);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = () => {
    // Call the callback to update the address in parent
    if (onAddressUpdate) {
      onAddressUpdate(newAddress);
    }
    setIsEditing(false);
  };

  const handleCancelClick = () => {
    setNewAddress(property.address);
    setIsEditing(false);
  };

  return (
    <div className="min-h-full relative isolate">
      {/* Unified Header without Breadcrumb */}
      <PageHeader
        title={
          <div className="flex items-center gap-5">
            <button
              onClick={onBack}
              className="flex items-center justify-center w-9 h-9 rounded-md border border-border bg-card hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2} />
            </button>
            {isEditing ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={newAddress}
                  onChange={(e) => setNewAddress(e.target.value)}
                  className="h-10 max-w-md"
                  autoFocus
                />
                <button
                  onClick={handleSaveClick}
                  className="flex items-center justify-center w-8 h-8 rounded-md bg-green-600 hover:bg-green-700 text-white transition-colors"
                >
                  <Check className="w-4 h-4" strokeWidth={2} />
                </button>
                <button
                  onClick={handleCancelClick}
                  className="flex items-center justify-center w-8 h-8 rounded-md bg-muted hover:bg-muted/80 transition-colors"
                >
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
            ) : (
              <>
                <span>{property.address}</span>
                <button
                  className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  onClick={handleEditClick}
                >
                  <Edit3 className="w-4 h-4" strokeWidth={2} />
                </button>
              </>
            )}
          </div>
        }
        actions={null}
        controls={
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={2} />
            <Input
              type="text"
              placeholder="Szukaj..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-8 text-[13px] bg-background border-border"
            />
          </div>
        }
      />

      {/* Masonry Gallery Grid */}
      <div className="px-10 py-10">
        <ResponsiveMasonry
          columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3, 1200: 4 }}
        >
          <Masonry gutter="36px">
            {/* Add Photo Placeholder - First Item */}
            <div className="relative mb-9 w-full">
              <AddPhotoPlaceholder onAddPhotoClick={onAddPhotoClick || (() => { })} />
            </div>

            {/* Generating Placeholder - Shows only when generating in THIS property AND it's a new generation (not rerender) */}
            {isGenerating && generatingPropertyId === property.id && !generatingImageId && (
              <div className="relative mx-2.5 mb-9 w-full">
                <GeneratingPhotoPlaceholder message="Generowanie renderów..." />
              </div>
            )}

            {galleryImages.map((image, index) => (
              <div
                key={index}
                className="relative mx-2.5 mb-9"
              >
                {/* Stack Effect - Only show if variantCount exists */}
                {image.variantCount != null && image.variantCount > 1 && (
                  <>
                    {/* Bottom layer (furthest back) */}
                    <div
                      className="absolute top-[12px] right-[-12px] w-full h-full rounded-[24px] bg-card/20 border border-border shadow-md"
                      style={{
                        zIndex: 0,
                        pointerEvents: 'none',
                      }}
                    />
                    {/* Middle layer */}
                    <div
                      className="absolute top-[6px] right-[-6px] w-full h-full rounded-[24px] bg-card/60 border border-border shadow-lg"
                      style={{
                        zIndex: 1,
                        pointerEvents: 'none',
                      }}
                    />
                  </>
                )}

                {/* Main Card */}
                <div className="relative group overflow-hidden rounded-[24px] bg-card border border-border transition-all duration-200 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-0.5" style={{ zIndex: 2 }}>
                  <img
                    src={image.url}
                    alt={`${property.address} - Photo ${index + 1}`}
                    className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                    style={{
                      aspectRatio: image.aspectRatio === 'square'
                        ? '1/1'
                        : '16/10'
                    }}
                    onClick={() => onPhotoClick?.(image.url, index, image.variantCount, image.roomName, image.variants, image.originalUrl, image.generationId)}
                  />

                  {/* Rerendering Overlay */}
                  {isGenerating && generatingImageId && (
                    (image as any).id === generatingImageId ||
                    (image as any).imageId === generatingImageId ||
                    image.originalUrl === generatingImageId // Fallback match
                  ) && (
                      <div className="absolute inset-0 z-20 bg-background/60 backdrop-blur-[2px] flex flex-col items-center justify-center animate-in fade-in duration-300">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-2" />
                        <span className="text-xs font-medium text-foreground">Renderowanie...</span>
                      </div>
                    )}

                  {/* Category Badge Overlay - Now with variant count */}
                  <div className="absolute top-3 left-3">
                    <PhotoBadge category={image.category} variantCount={image.variantCount} generationError={image.generationError} />
                  </div>

                  {/* More Options Button - Top Right */}
                  <div className="absolute top-3 right-3 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity flex flex-col gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="flex items-center justify-center w-8 h-8 rounded-md bg-card/90 hover:bg-card backdrop-blur-sm shadow-sm transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-48">
                        <DropdownMenuItem
                          className="gap-2 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPhotoClick?.(image.url, index, image.variantCount, image.roomName, image.variants, image.originalUrl, image.generationId);
                          }}
                        >
                          <Eye className="w-4 h-4" strokeWidth={2} />
                          <span>Podgląd</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadImageWithWatermarks(
                              image.url,
                              `${property.address}_photo_${index + 1}.jpg`,
                            ).catch(err => console.error('Download error:', err));
                          }}
                        >
                          <Download className="w-4 h-4" strokeWidth={2} />
                          <span>Pobierz</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            alert('Funkcja udostępniania będzie dostępna wkrótce');
                          }}
                        >
                          <Share2 className="w-4 h-4" strokeWidth={2} />
                          <span>Udostępnij</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="gap-2 cursor-pointer text-destructive focus:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFileToDelete({ url: image.url, name: image.roomName || 'Zdjęcie' });
                          }}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={2} />
                          <span>Usuń</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Heart Button */}
                    <button
                      className={`flex items-center justify-center w-8 h-8 rounded-md bg-card/90 hover:bg-card backdrop-blur-sm shadow-sm transition-colors ${image.favorite ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite?.(image.url);
                      }}
                    >
                      <Heart className={`w-4 h-4 ${image.favorite ? 'fill-current' : ''}`} strokeWidth={2} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </Masonry>
        </ResponsiveMasonry>
      </div>

      <DeleteFileDialog
        open={!!fileToDelete}
        onOpenChange={(open) => !open && setFileToDelete(null)}
        fileName={fileToDelete?.name || ''}
        onConfirm={() => {
          if (fileToDelete && onDeletePhoto) {
            onDeletePhoto(fileToDelete.url);
          }
          setFileToDelete(null);
        }}
      />
    </div>
  );
}